<template>
  <div
    class="zm-multi-select-dropdown zm-popper"
    tabindex="0"
    role="dialog"
    aria-label="multi select dropdown"
    @keydown.esc="$emit('apply-close')"
  >
    <header>
      <zm-input
        v-if="searchVisible"
        v-model="filterText"
        class="filter-text"
        size="small"
        :label="searchPlaceholder"
        :placeholder="searchPlaceholder"
        prefix-icon="zm-icon-search"
        @keydown.enter.native="triggerRemoteSearch"
        @blur="triggerRemoteSearch"
      />
      <zm-checkbox
        v-if="filterAllVisible && filterText.trim().length === 0"
        v-model="isSelectAll"
        :indeterminate="isIndeterminate"
        :label="filterAllLabel"
        :controls="options.map(item => `zm-ms-${item[valueKey]}`).join(' ')"
        @change="handleCheckAllChange"
      >
        {{ filterAllLabel }}
      </zm-checkbox>
      <slot name="header" />
    </header>
    <zm-scrollbar
      ref="scrollbar"
      wrap-style="min-height: 70px;max-height: 200px;"
      @scroll-end="triggerLoadData"
    >
      <zm-checkbox-list
        v-model="selectedIdList"
        :items="screenOptions"
        :column-count="1"
        :span="24"
        aria-label="options list"
        @change="handleSelectedChange"
      >
        <template slot-scope="props">
          <slot
            name="option"
            :item="props.item._origin"
          />
        </template>
      </zm-checkbox-list>
      <div
        v-if="hasMore"
        v-loading="loading"
        class="loading-tips"
      />
    </zm-scrollbar>
    <footer v-if="$slots.footer || buttonVisible">
      <slot name="footer" />
      <div v-if="buttonVisible" class="button-footer">
        <span v-if="$slots.applyButton" @click="submitSelected">
          <slot name="applyButton" />
        </span>
        <zm-button v-else @click="submitSelected">
          {{ confirmLabel }}
        </zm-button>

        <template v-if="clearVisible">
          <span v-if="$slots.clearButton" @click="clearSelect">
            <slot name="clearButton" />
          </span>
          <zm-button
            v-else
            type="plain"
            ghost
            @click="clearSelect"
          >
            {{ clearLabel }}
          </zm-button>
        </template>
      </div>
    </footer>
  </div>
</template>

<script>
  import { debounce } from 'throttle-debounce';
  import Popper from '../../utils/vue-popper';
  import AriaDialog from '../../utils/aria-dialog';

  export default {
    name: 'ZmMultiSelectDropdown',
    mixins: [Popper],
    props: {
      placement: {
        type: String,
        default: 'bottom-start'
      },
      initSelect: {
        type: Array,
        default() {
          return [];
        }
      },
      options: {
        type: Array,
        default() {
          return [];
        }
      },
      valueKey: {
        type: String,
        default: ''
      },
      nameKey: {
        type: String,
        default: ''
      },
      disabledKey: {
        type: String,
        default: 'disabled'
      },
      clearVisible: {
        type: Boolean,
        default: true
      },
      searchVisible: {
        type: Boolean,
        default: true
      },
      remoteSearch: Boolean,
      buttonVisible: {
        type: Boolean,
        default: true
      },
      filterAllVisible: {
        type: Boolean,
        default: true
      },
      searchPlaceholder: {
        type: String,
        default: ''
      },
      filterAllLabel: {
        type: String,
        default: ''
      },
      confirmLabel: {
        type: String,
        default: ''
      },
      clearLabel: {
        type: String,
        default: ''
      },
      hasMore: Boolean,
      loading: Boolean
    },
    data() {
      return {
        filterText: '',
        isSelectAll: false,
        selectedIdList: [],
        isIndeterminate: false
      };
    },
    computed: {
      screenOptions() {
        const options = this.getFilteredOptions();
        return options.map(item => {
          return {
            displayName: item[this.nameKey],
            label: item[this.valueKey],
            ariaLabel: item[this.nameKey],
            disabled: item[this.disabledKey],
            _origin: item
          };
        });
      }
    },
    watch: {
      options: {
        handler() {
          this.debounceCheckLoadMore();
          this.initSelectStatus();
        },
        immediate: true,
        deep: true
      }
    },
    beforeCreate() {
      this.debounceCheckLoadMore = debounce(20, () => {
        const scrollbarRef = this.$refs.scrollbar;
        if (!scrollbarRef) {
          return;
        }
        // check if no scrollbar
        if (scrollbarRef.wrap.scrollHeight === scrollbarRef.wrap.clientHeight) {
          this.triggerLoadData();
        }
        // trigger scrollbar update
        scrollbarRef && scrollbarRef.update();
      });
    },
    mounted() {
      // only apply props selected once
      this.selectedIdList = this.initSelect;
      this.initSelectStatus();
      this.initPopper();
    },
    beforeDestroy() {
      this.destroyPopper();
    },
    methods: {
      initAriaDialog() {
        const focusAfterClosed = document.activeElement;
        const firstFocus = this.getFirstFocus();
        this.ariaDialog = new AriaDialog(this.$el, focusAfterClosed, firstFocus);
      },
      closeAriaDialog() {
        this.ariaDialog && this.ariaDialog.closeDialog();
        this.ariaDialog = null;
      },
      getFirstFocus() {
        const focusFirst = this.$el.querySelector('input');
        if (focusFirst) {
          return focusFirst;
        }
        return null;
      },
      getFilteredOptions() {
        if (this.remoteSearch) {
          return this.options;
        }
        const filterText = (this.filterText || '').trim().toLowerCase();
        if (filterText.length === 0) {
          return this.options;
        }
        return this.options.filter(item => {
          const label = (item[this.nameKey] || '').toLowerCase();
          return label.indexOf(filterText) > -1;
        });
      },
      initSelectStatus() {
        const checkedCount = this.selectedIdList.length;
        const optionsLength = this.options.length;
        this.isSelectAll = checkedCount === optionsLength && checkedCount !== 0;
        this.isIndeterminate = checkedCount > 0 && checkedCount < optionsLength;
      },
      initPopper() {
        this.referenceElm = this.$parent.$el;
        this.$parent.popperElm = this.popperElm = this.$el;
        this.updatePopper();
      },
      handleCheckAllChange(val) {
        const selectedIdList = [];

        this.options.forEach((item) => {
          const itemId = item[this.valueKey];
          const itemDisabled = item[this.disabledKey];
          const isSelectedBefore = this.selectedIdList.indexOf(itemId) > -1;
          if (val) {
            if (
              !itemDisabled ||
              (itemDisabled && isSelectedBefore)
            ) {
              selectedIdList.push(itemId);
            }
          } else {
            if (itemDisabled && isSelectedBefore) {
              selectedIdList.push(itemId);
            }
          }
        });
        this.selectedIdList = selectedIdList;
        this.isIndeterminate = false;
        this.submitIfButtonInvisible();
      },
      handleSelectedChange(value) {
        const checkedCount = value.length;
        this.isSelectAll = checkedCount === this.options.length;
        this.isIndeterminate = checkedCount > 0 && checkedCount < this.options.length;
        this.submitIfButtonInvisible();
      },
      triggerRemoteSearch() {
        if (this.remoteSearch) {
          // stop remote search when filter string did not changed
          const filterText = this.filterText.trim();
          if (filterText === (this._lastFilterText || '')) {
            return;
          }
          this._lastFilterText = filterText;
          this.triggerLoadData();
        }
      },
      triggerLoadData() {
        let searchText = '';
        if (this.remoteSearch) {
          searchText = this.filterText.trim();
        }
        this.$emit('load-data', searchText);
      },
      clearSelect() {
        this.$emit('data-change', []);
        this.$emit('apply-close');
      },
      submitIfButtonInvisible() {
        if (!this.buttonVisible) {
          this.$emit('data-change', this.selectedIdList);
        }
      },
      submitSelected() {
        this.$emit('data-change', this.selectedIdList);
        this.$emit('apply-close');
      }
    }
  };
</script>

<style lang='scss'>
  @import "../../scss/common/popper";
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(multi-select-dropdown) {
    min-width: 140px;
    outline: 0;
    position: absolute;
    z-index: #{$--index-top + 1};
    margin: 4px 0 0;
    border-radius: $--select-dropdown-border-radius;
    background-color: $--select-dropdown-background;
    box-shadow: $--select-dropdown-shadow;
    box-sizing: border-box;
    header {
      padding: 16px 0 8px;
      margin: 0 16px;
      border-bottom: 1px solid #efeff4;
      .zm-input {
        margin-bottom: 5px;
      }
    }
    .zm-checkbox-list {
      margin: 5px 0 5px 12px;
    }
    .zm-checkbox {
      display: block;
    }
    .loading-tips {
      height: 30px;
      .zm-loading-mask {
        background: transparent;
      }
      .zm-loading-spinner {
        margin-top: -10px;
      }
      svg {
        width: 16px;
        height: 16px;
      }
    }

    .zm-scrollbar {
      margin: 5px 0;
    }
    .button-footer {
      padding: 11px 15px 13px;
      border-top: 1px solid #efeff4;
    }
    &:focus {
      outline: 1px solid $--color-primary;
    }
  }

  .zm-theme--dark {
    @include b(multi-select-dropdown) {
      background-color: #131619;
      box-shadow: 0px 6px 12px 0px black, 0px 12px 24px 0px black;
      border: 1px solid #444b53;
      @include e(item) {
        border-color: #252a30;
      }
      header {
        border-bottom-color: #2e3138
      }
      .button-footer {
        border-top-color: #2e3138
      }
    }
  }
</style>
